import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { DndContext, DragOverlay, useDraggable, useDroppable, closestCenter } from "@dnd-kit/core";
import { Trophy, Clock, Users, CheckCircle2, XCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

// Kern Cycle elements
const KERN_ELEMENTS = [
  { id: 1, text: "Problem identification and general needs assessment", color: "#FF3B30", shape: "circle" },
  { id: 2, text: "Targeted needs assessment", color: "#FF9500", shape: "square" },
  { id: 3, text: "Goals and objectives", color: "#FFCC00", shape: "pill" },
  { id: 4, text: "Educational strategies", color: "#4CD964", shape: "hexagon" },
  { id: 5, text: "Implementation", color: "#5AC8FA", shape: "triangle" },
  { id: 6, text: "Evaluating the effectiveness of the curriculum", color: "#007AFF", shape: "diamond" }
];

const DraggableElement = ({ element, isDragging }) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: `draggable-${element.id}`,
    data: element
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
  } : undefined;

  const shapeClass = {
    circle: "rounded-full",
    square: "rounded-none",
    pill: "rounded-full",
    hexagon: "rounded-lg",
    triangle: "rounded-lg",
    diamond: "rounded-lg"
  }[element.shape];

  return (
    <div
      ref={setNodeRef}
      style={{ ...style, backgroundColor: element.color }}
      className={`${shapeClass} border-4 border-[#121212] p-4 cursor-grab active:cursor-grabbing neo-shadow-sm ${isDragging ? 'opacity-50 scale-105 rotate-3' : ''} transition-transform min-h-[100px] flex items-center justify-center`}
      {...listeners}
      {...attributes}
      data-testid={`draggable-shape-${element.id}`}
    >
      <p className="text-xs font-black text-white text-center leading-tight" style={{ textShadow: '2px 2px 0px #121212' }}>
        {element.text}
      </p>
    </div>
  );
};

const DroppableZone = ({ id, element, feedback }) => {
  const { setNodeRef, isOver } = useDroppable({ id });

  const getFeedbackClass = () => {
    if (feedback === 'correct') return 'pop bg-[#00E676] border-[#121212]';
    if (feedback === 'incorrect') return 'shake bg-[#FF1744] border-[#121212]';
    return isOver ? 'bg-[#FFD500]/20 border-[#121212]' : 'bg-[#121212]/5 border-[#121212]/50';
  };

  return (
    <div
      ref={setNodeRef}
      className={`border-4 border-dashed ${getFeedbackClass()} p-6 rounded-lg min-h-[120px] flex items-center justify-center relative transition-all`}
      data-testid={`dropzone-${id}`}
    >
      {element ? (
        <div 
          className="absolute inset-0 rounded-lg border-4 border-[#121212] flex items-center justify-center p-4"
          style={{ backgroundColor: element.color }}
        >
          <div className="text-center">
            <p className="text-2xl font-black text-white mb-2" style={{ textShadow: '2px 2px 0px #121212' }}>
              {element.id}
            </p>
            <p className="text-xs font-bold text-white" style={{ textShadow: '1px 1px 0px #121212' }}>
              {element.text}
            </p>
          </div>
          {feedback === 'correct' && (
            <CheckCircle2 className="absolute top-2 right-2 text-white" size={24} strokeWidth={3} />
          )}
          {feedback === 'incorrect' && (
            <XCircle className="absolute top-2 right-2 text-white" size={24} strokeWidth={3} />
          )}
        </div>
      ) : (
        <p className="text-sm font-bold text-[#121212]/50 uppercase tracking-wider">
          Drop Here
        </p>
      )}
    </div>
  );
};

const Game = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { playerId, playerName } = location.state || {};
  
  const [ws, setWs] = useState(null);
  const [availableElements, setAvailableElements] = useState([...KERN_ELEMENTS]);
  const [placedElements, setPlacedElements] = useState(Array(6).fill(null));
  const [activeId, setActiveId] = useState(null);
  const [feedback, setFeedback] = useState(Array(6).fill(null));
  const [timer, setTimer] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [otherPlayers, setOtherPlayers] = useState([]);
  const timerRef = useRef(null);
  const startTimeRef = useRef(Date.now());

  useEffect(() => {
    if (!playerId || !playerName) {
      navigate('/');
      return;
    }

    // Connect WebSocket
    const websocket = new WebSocket(`${WS_URL}/api/ws/${playerId}`);
    setWs(websocket);

    websocket.onopen = () => {
      websocket.send(JSON.stringify({ player_name: playerName }));
    };

    websocket.onmessage = (event) => {
      const message = JSON.parse(event.data);

      if (message.type === "game_state") {
        const players = Object.entries(message.data.players)
          .filter(([id]) => id !== playerId)
          .map(([id, data]) => ({
            id,
            name: data.name,
            progress: data.progress
          }));
        setOtherPlayers(players);
      }

      if (message.type === "player_finished") {
        console.log(`${message.data.player_name} finished in ${message.data.finish_time}s!`);
      }
    };

    // Start timer
    startTimeRef.current = Date.now();
    timerRef.current = setInterval(() => {
      setTimer(((Date.now() - startTimeRef.current) / 1000).toFixed(1));
    }, 100);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (websocket) websocket.close();
    };
  }, [playerId, playerName, navigate]);

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const draggedElementId = active.id.replace('draggable-', '');
    const draggedElement = KERN_ELEMENTS.find(el => el.id === parseInt(draggedElementId));
    const dropZoneIndex = parseInt(over.id.replace('dropzone-', ''));

    if (!draggedElement) return;

    // Check if valid placement
    const isValidPlacement = checkValidPlacement(draggedElement, dropZoneIndex);
    
    // Update feedback
    const newFeedback = [...feedback];
    newFeedback[dropZoneIndex] = isValidPlacement ? 'correct' : 'incorrect';
    setFeedback(newFeedback);

    if (isValidPlacement) {
      // Place the element
      const newPlaced = [...placedElements];
      newPlaced[dropZoneIndex] = draggedElement;
      setPlacedElements(newPlaced);

      // Remove from available
      setAvailableElements(availableElements.filter(el => el.id !== draggedElement.id));

      // Update progress
      const progress = Math.round((newPlaced.filter(Boolean).length / 6) * 100);
      if (ws) {
        ws.send(JSON.stringify({ type: "progress_update", progress }));
      }

      // Check if complete
      if (newPlaced.filter(Boolean).length === 6) {
        completeGame();
      }
    }

    // Clear feedback after animation
    setTimeout(() => {
      setFeedback(Array(6).fill(null));
    }, 500);
  };

  const checkValidPlacement = (element, index) => {
    // Check if the slot is empty
    if (placedElements[index] !== null) return false;

    // If it's the first placement, any position is valid
    if (placedElements.every(el => el === null)) return true;

    // Find the positions of placed elements
    const placedPositions = placedElements.map((el, idx) => el ? idx : null).filter(pos => pos !== null);
    
    // Check if this element can go in sequence
    // It should be adjacent to an existing element and maintain order
    for (let pos of placedPositions) {
      const placedEl = placedElements[pos];
      
      // Check if placing to the right (next in sequence)
      if (index === pos + 1 && element.id === getNextId(placedEl.id)) {
        return true;
      }
      
      // Check if placing to the left (previous in sequence)
      if (index === pos - 1 && element.id === getPrevId(placedEl.id)) {
        return true;
      }
    }

    return false;
  };

  const getNextId = (id) => id === 6 ? 1 : id + 1;
  const getPrevId = (id) => id === 1 ? 6 : id - 1;

  const completeGame = async () => {
    if (isComplete) return;
    
    clearInterval(timerRef.current);
    const finalTime = parseFloat(timer);
    setIsComplete(true);

    // Send finish event
    if (ws) {
      ws.send(JSON.stringify({ type: "finished", finish_time: finalTime }));
    }

    // Save to leaderboard
    try {
      await axios.post(`${API}/leaderboard`, {
        player_name: playerName,
        completion_time: finalTime
      });
    } catch (error) {
      console.error("Error saving to leaderboard:", error);
    }

    // Navigate to leaderboard after 2 seconds
    setTimeout(() => {
      navigate('/leaderboard');
    }, 2000);
  };

  const activeElement = activeId ? KERN_ELEMENTS.find(el => `draggable-${el.id}` === activeId) : null;

  return (
    <div 
      className="min-h-screen p-6"
      style={{
        backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/1cd2d0b9-b28c-4677-afeb-8f22de75f592/images/846d1d91f9f98a35c335c7ca4cd8b96cfad76204f5dc23965abb55750b1c3a52.png')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
      data-testid="game-screen"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8 flex-wrap gap-4">
          <div className="bg-white border-4 border-[#121212] px-6 py-3 neo-shadow" data-testid="timer-display">
            <Clock className="inline mr-2" strokeWidth={3} />
            <span className="text-2xl font-black">{timer}s</span>
          </div>
          
          <h1 
            className="text-4xl tracking-tighter uppercase text-[#121212]"
            style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 900 }}
          >
            {playerName}
          </h1>

          <div className="bg-white border-4 border-[#121212] px-6 py-3 neo-shadow" data-testid="players-count">
            <Users className="inline mr-2" strokeWidth={3} />
            <span className="text-lg font-black">{otherPlayers.length + 1} Racing</span>
          </div>
        </div>

        {/* Other Players Progress */}
        {otherPlayers.length > 0 && (
          <div className="bg-white border-4 border-[#121212] p-4 mb-6 neo-shadow" data-testid="other-players-progress">
            <p className="text-sm uppercase tracking-[0.15em] mb-3 font-bold">Other Players</p>
            <div className="space-y-2">
              {otherPlayers.map((player) => (
                <div key={player.id} className="flex items-center gap-3">
                  <span className="font-bold text-sm w-32 truncate">{player.name}</span>
                  <div className="flex-1 bg-[#121212]/10 border-2 border-[#121212] h-6 relative overflow-hidden">
                    <div 
                      className="absolute inset-y-0 left-0 bg-[#0055FF] transition-all duration-300"
                      style={{ width: `${player.progress}%` }}
                    />
                  </div>
                  <span className="font-bold text-sm w-12">{player.progress}%</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <DndContext 
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          collisionDetection={closestCenter}
        >
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Available Elements */}
            <div className="bg-white border-4 border-[#121212] p-6 neo-shadow" data-testid="available-elements">
              <h2 
                className="text-2xl sm:text-3xl tracking-tight uppercase mb-6 text-[#121212]"
                style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 700 }}
              >
                KERN CYCLE ELEMENTS
              </h2>
              <div className="grid grid-cols-2 gap-4">
                {availableElements.map((element) => (
                  <DraggableElement 
                    key={element.id} 
                    element={element}
                    isDragging={activeId === `draggable-${element.id}`}
                  />
                ))}
              </div>
              {availableElements.length === 0 && (
                <p className="text-center text-lg font-bold text-[#00E676] mt-4">All elements placed!</p>
              )}
            </div>

            {/* Drop Zones */}
            <div className="bg-white border-4 border-[#121212] p-6 neo-shadow" data-testid="drop-zones">
              <h2 
                className="text-2xl sm:text-3xl tracking-tight uppercase mb-6 text-[#121212]"
                style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 700 }}
              >
                ARRANGE IN ORDER
              </h2>
              <div className="space-y-4">
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <DroppableZone 
                    key={index}
                    id={`dropzone-${index}`}
                    element={placedElements[index]}
                    feedback={feedback[index]}
                  />
                ))}
              </div>
            </div>
          </div>

          <DragOverlay>
            {activeElement ? (
              <div 
                className="rounded-lg border-4 border-[#121212] p-4 neo-shadow-sm opacity-80 rotate-3 scale-105 min-h-[100px] flex items-center justify-center"
                style={{ backgroundColor: activeElement.color }}
              >
                <p className="text-xs font-black text-white text-center leading-tight" style={{ textShadow: '2px 2px 0px #121212' }}>
                  {activeElement.text}
                </p>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>

        {/* Completion Modal */}
        {isComplete && (
          <div className="fixed inset-0 bg-[#121212]/80 flex items-center justify-center z-50" data-testid="completion-modal">
            <div className="bg-[#FFD500] border-4 border-[#121212] p-8 neo-shadow max-w-md text-center pop">
              <Trophy className="mx-auto mb-4" size={64} strokeWidth={3} />
              <h2 
                className="text-4xl tracking-tight uppercase mb-4 text-[#121212]"
                style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 900 }}
              >
                COMPLETE!
              </h2>
              <p className="text-2xl font-black mb-2">Time: {timer}s</p>
              <p className="text-lg font-bold">Redirecting to leaderboard...</p>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="mt-8 bg-white border-4 border-[#121212] p-6 neo-shadow" data-testid="game-instructions">
          <p className="text-sm font-bold text-[#333333]">
            <strong>💡 Tip:</strong> Start with any element, then place the next in sequence. The cycle can begin anywhere but must flow in order (1→2→3→4→5→6→1...)!
          </p>
        </div>
      </div>
    </div>
  );
};

export default Game;
